import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSSelect } from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  DEFAULT_VALUES,
  ATTRIBUTE_CATEGORY_OPTIONS,
  DICT_SELECT_PROPS,
} from '../../constants/personFeatureFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonFeature, editPersonFeature } from '../../services/userService';

const PersonFeatureDialog = ({ open, onClose, personId, feature = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(feature?.ptsFeatureID);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(feature || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const { dictionary } = useUserDictionaryOptions();
  const attributeCategory = useWatch({ control: methods.control, name: 'AttributeCategory' });
  const attribute = useWatch({ control: methods.control, name: 'Attribute' });

  const attributeOptions = useMemo(() => dictionary?.[attributeCategory] ?? [], [
    dictionary,
    attributeCategory,
  ]);

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        AttributeCategory: data.AttributeCategory,
        Attribute: data.Attribute,
      };

      if (isEditMode) {
        payload.UpdatedBy = currentUser?.Username;
      } else {
        payload.ptsPersonID = personId;
        payload.CreatedBy = currentUser?.Username;
        payload.UpdatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? editPersonFeature(feature.ptsFeatureID, payload)
            : createPersonFeature(payload),
        onSuccess: (savedFeature) => {
          dispatch(
            notify(
              isEditMode ? 'Feature updated successfully' : 'Feature created successfully',
              'success'
            )
          );
          onSaved?.(savedFeature);
        },
      });
    },
    [isEditMode, feature, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this feature. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Feature' : 'Add Feature'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting || !attribute}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!feature?.Created}
            created={feature?.Created}
            createdBy={feature?.CreatedBy}
            updated={feature?.Updated}
            updatedBy={feature?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RMSSelect
              name="AttributeCategory"
              label="Attribute Category"
              options={ATTRIBUTE_CATEGORY_OPTIONS}
              {...DICT_SELECT_PROPS}
              onChange={() => methods.setValue('Attribute', '')}
            />
          </Grid>
          {attributeCategory && (
            <Grid item xs={12}>
              <RMSSelect
                name="Attribute"
                label="Attribute"
                options={attributeOptions}
                {...DICT_SELECT_PROPS}
                showLabelOnly={false}
              />
            </Grid>
          )}
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonFeatureDialog;
